import { createFileRoute, Link } from "@tanstack/react-router";
import { Building2, CalendarCheck, ChevronRight, Heart, Target } from "lucide-react";

import { useMe, usePickedEvents, useProfile, useSettings } from "@/lib/queries";
import { GOAL_LABELS, type GoalType } from "@omina/core";

/**
 * Her profile — the front door to everything Omina knows about her.
 *
 * Three rows today, and deliberately only three. This is a *directory*, not a
 * dashboard: every line here opens something that already exists somewhere
 * else in the product, so the screen's job is to make those findable rather
 * than to restate them. A profile page that summarises what is behind it ends
 * up being the fourth place the same buyer sentence lives, and then three of
 * the four are stale.
 *
 * The picture is her Google avatar, which arrives with sign-in and is the one
 * image in the product that is genuinely hers. Nothing is uploaded and nothing
 * is stored: it is a URL on the `user` row that better-auth wrote.
 */
export const Route = createFileRoute("/profile")({
  component: Profile,
});

function Profile() {
  const me = useMe();
  const profile = useProfile();
  const settings = useSettings();

  /**
   * Rooms already behind her.
   *
   * A year back, so "past events" means her history rather than this month's.
   * The count is shown only when it is real — a row reading "0 rooms" under a
   * heading about her past is worse than the row saying nothing, because it
   * reads as a product that lost them.
   */
  const past = usePickedEvents(365);
  const now = Date.now();
  const pastCount = (past.data ?? []).filter(
    (p) => new Date(p.date).getTime() < now,
  ).length;

  const user = me.data?.user;
  const org = me.data?.organization;

  return (
    <div className="profwrap">
      <header className="profhead">
        {user?.image ? (
          <img className="profpic" src={user.image} alt="" referrerPolicy="no-referrer" />
        ) : (
          // Google is the only way in, so an avatar almost always exists —
          // but "almost" is why initials are here rather than a broken image.
          <span className="profpic profinitials" aria-hidden>
            {initials(user?.name)}
          </span>
        )}
        <h1 className="profname">{user?.name ?? "You"}</h1>
        {org?.name && <p className="profwhere">{org.name}</p>}
      </header>

      <ul className="proflist">
        {/* The workspace card, which is where the company facts actually
            live — not /settings, which is now a menu. */}
        <Row
          to="/settings/connections"
          icon={<Building2 size={17} aria-hidden />}
          label="Company profile"
          sub={profile.data?.product ? truncate(profile.data.product) : "What you build, and for whom"}
        />
        {/* The read view, not the questions. This row is for consulting what
            Omina knows; the conversation that writes it is one tap further in,
            behind a button that says it edits. */}
        <Row
          to="/icp-profile"
          icon={<Target size={17} aria-hidden />}
          label="ICP blueprint"
          sub={
            profile.data?.buyerRole
              ? `${profile.data.buyerRole}${profile.data.buyerCompany ? ` at ${profile.data.buyerCompany}` : ""}`
              : "Who actually buys"
          }
        />
        {/* The two Settings sends here. They are not plumbing — they are what
            she is going after and what she likes — so they live with the rest
            of who she is rather than in a second goal picker under Settings. */}
        <Row
          to="/path"
          icon={<Target size={17} aria-hidden />}
          label="Your path"
          sub={goalLine(settings.data?.activeGoal)}
        />
        <Row
          to="/interests"
          icon={<Heart size={17} aria-hidden />}
          label="Your interests"
          sub="The rooms and topics you want"
        />
        <Row
          to="/calendar"
          icon={<CalendarCheck size={17} aria-hidden />}
          label="Past events"
          sub={
            pastCount > 0
              ? `${pastCount} ${pastCount === 1 ? "room" : "rooms"} behind you`
              : "Rooms you have been in"
          }
        />
      </ul>
    </div>
  );
}

function Row({
  to,
  icon,
  label,
  sub,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  sub: string;
}) {
  return (
    <li>
      <Link to={to} className="profrow">
        <span className="proficon">{icon}</span>
        <span className="proftext">
          <b>{label}</b>
          <span>{sub}</span>
        </span>
        <ChevronRight size={17} className="profchev" aria-hidden />
      </Link>
    </li>
  );
}

/** The current track, named — or a prompt when nothing is set. */
function goalLine(goal?: GoalType | null): string {
  return goal ? GOAL_LABELS[goal].title : "Not chosen yet";
}

/** Up to two letters, which is all the circle has room for. */
function initials(name?: string | null): string {
  if (!name) return "·";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}

/** One line, so a three-sentence product description does not turn a row into
 *  a paragraph. */
function truncate(text: string, max = 58): string {
  const clean = text.trim();
  return clean.length <= max ? clean : `${clean.slice(0, max - 1).trimEnd()}…`;
}
